/**
 * What a number box shows. Pure, so the rules are tested apart from React.
 *
 * Owner, 2026-11-03: "a lot of inputs by default there is zero… if cleared I
 * don't need any numbers there". Two things were wrong with every controlled
 * number box:
 *  - a value of 0 (most forms start there) was printed as "0", so typing 5
 *    gave "05" or "50";
 *  - emptying the box could not stick: the form turns "" into 0
 *    (`Number("") === 0`, `parseInt(v) || 0`), and React then writes "0" back
 *    into an empty number input whose value is 0.
 *
 * The rules: at rest, 0 and "nothing" both read as an empty box. While someone
 * is typing, the box shows exactly what they typed, even when the form stores
 * something else for it (0 for an empty box, a minimum, a clamp). On leaving
 * the box it shows what the form actually holds again. The VALUE handed to the
 * form is never touched, so what gets saved is what was saved before.
 */

/**
 * An empty box for null / undefined / "" / NaN and — unless `keepZero` — any
 * zero ("0", "0.00", -0). `keepZero` is for a box where 0 is a choice someone
 * makes rather than "nothing yet" (a condition "score is 0", "0 days before"),
 * whose form keeps an emptied box empty instead of storing 0.
 */
export function restingNumberText(value: unknown, keepZero = false): string {
  if (value == null) return "";
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "";
    return value === 0 && !keepZero ? "" : String(value);
  }
  const text = String(value);
  if (text.trim() === "") return "";
  return Number(text) === 0 && !keepZero ? "" : text;
}

/** Does the form's value still say what the person typed? ("" matches 0 / nothing.) */
export function typedMatchesValue(typed: string, value: unknown): boolean {
  const t = typed.trim();
  if (t === "") return restingNumberText(value) === "";
  if (value == null || (typeof value === "string" && value.trim() === "")) return false;
  const typedNum = Number(t);
  const valueNum = Number(value);
  return Number.isFinite(typedNum) && Number.isFinite(valueNum) && typedNum === valueNum;
}

/**
 * The text to put in the box.
 *
 * `typed` is what the person typed since focusing it (null = they haven't).
 * `answered` is true when the value is the form's answer to their last
 * keystroke — whatever it turned it into (0 for an empty box, a clamp, a
 * minimum). Their text wins then; a value that changed for any OTHER reason
 * (a reset, a "fill" button, a recalculation) wins over the typed text.
 */
export function numberBoxText(
  value: unknown,
  typed: string | null,
  answered: boolean,
  keepZero = false,
): string {
  if (typed !== null && (answered || typedMatchesValue(typed, value))) return typed;
  return restingNumberText(value, keepZero);
}
